import type { CookieOptions } from '#app'
import type { SortOptions } from '@/components/table/SkrServerTable.vue'

export interface SharedDashboardViewState {
  view: string
  pageSize: number
}

export interface SignatureRequestViewState {
  filters: DashboardFilter[]
  dates: Date[]
  currentTimeMode: boolean
  sentByMe: boolean
  sort: SortOptions
}

export type DashboardViewState = SharedDashboardViewState & SignatureRequestViewState

type DashboardSessionOptions = Pick<SignatureRequestPaginationRequest, 'sortBy' | 'sortOrder' | 'page' | 'size'>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDefaultCookieConfig = <T = any>(): CookieOptions<T> & { readonly?: false } => ({
  maxAge: 365 * 24 * 60 * 60,
  sameSite: 'strict',
  encode: value => encodeURIComponent(JSON.stringify(value)),
  decode: value => JSON.parse(decodeURIComponent(value || '{}')),
})

const encodeSrViewState = (state: SignatureRequestViewState): string => {
  return encodeURIComponent(
    JSON.stringify({
      ...state,
      dates: Array.isArray(state.dates) ? state.dates.map(date => date.toISOString()) : [],
    })
  )
}

const decodeSrViewState = (encodedState: string): SignatureRequestViewState => {
  try {
    const parsed = JSON.parse(decodeURIComponent(encodedState))
    return {
      ...parsed,
      dates: Array.isArray(parsed.dates) ? parsed.dates.map((dateStr: string) => new Date(dateStr)) : [],
    }
  } catch {
    return {
      filters: [],
      dates: [],
      currentTimeMode: false,
      sentByMe: false,
      sort: {
        sortBy: [],
      },
    }
  }
}

export const useDashboardStore = defineStore('dashboard', () => {
  const lastSelectedTab = ref<DashboardTab | null>(null)

  const defaults: DashboardViewState = {
    view: isMobileDevice() ? 'grid' : 'list',
    pageSize: 25,
    filters: [],
    dates: [],
    currentTimeMode: false,
    sentByMe: false,
    sort: {
      sortBy: [],
    },
  }

  // TODO: Supersede util member
  const paginationOptions = [
    { value: 25, title: '25' },
    { value: 50, title: '50' },
    { value: 100, title: '100' },
  ]

  const lastSelectedSessionOptions = ref<DashboardSessionOptions | null>(null)

  const userStore = useUserStore()

  const srConfig: ReturnType<typeof getDefaultCookieConfig<SignatureRequestViewState>> = {
    ...getDefaultCookieConfig(),
    encode: encodeSrViewState,
    decode: decodeSrViewState,
    default() {
      const { view, pageSize, ...specificOptions } = defaults
      return specificOptions
    },
  }

  const inboxState = useCookie<SignatureRequestViewState>(`view-inbox-${userStore.id}`, srConfig)
  const draftsState = useCookie<SignatureRequestViewState>(`view-drafts-${userStore.id}`, srConfig)
  const sharedState = useCookie<SharedDashboardViewState>(`view-shared`, {
    ...getDefaultCookieConfig(),
    default() {
      return { view: defaults.view, pageSize: defaults.pageSize }
    },
  })

  const persistDashboardState = (tab: DashboardTab, state: Partial<DashboardViewState>) => {
    try {
      const { view, pageSize, ...tabSpecificUpdates } = state

      if (view || pageSize) {
        const newSharedState = { ...sharedState.value }
        if (view) newSharedState.view = view
        if (pageSize) newSharedState.pageSize = pageSize
        sharedState.value = newSharedState
      }

      if (!Object.keys(tabSpecificUpdates).length) return

      if (tab === 'inbox') {
        const newInboxState = { ...inboxState.value }
        Object.assign(newInboxState, tabSpecificUpdates)
        inboxState.value = newInboxState
      } else if (tab === 'drafts') {
        const newDraftsState = { ...draftsState.value }
        Object.assign(newDraftsState, tabSpecificUpdates)
        draftsState.value = newDraftsState
      }
    } catch (error) {
      console.error('Failed to save view state:', error)
    }
  }

  return {
    lastSelectedTab,
    paginationOptions,
    lastSelectedSessionOptions,
    defaults,
    inboxState,
    draftsState,
    sharedState,
    persistDashboardState,
  }
})
